import React, { useState, useEffect } from 'react';
import ThemeVersesPage from './ThemeVersesPage';
import CharacterHistoryPage from './CharacterHistoryPage';

const BibleConcordancePage = ({ onGoBack, showOnlyCharacters = false, showOnlyThemes = false }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [results, setResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [concordanceSource, setConcordanceSource] = useState("levangile"); // "levangile" ou "topbible"
  
  // Plus besoin de ces états - les personnages ouvrent maintenant une page dédiée
  const [selectedTheme, setSelectedTheme] = useState(null);
  const [selectedCharacterForHistory, setSelectedCharacterForHistory] = useState(null);
  const [currentTab, setCurrentTab] = useState('concordance');

  // Si showOnlyCharacters est true, forcer l'onglet personnages
  useEffect(() => {
    if (showOnlyCharacters) {
      setCurrentTab('personnages');
    } else if (showOnlyThemes) {
      setCurrentTab('themes');
    }
  }, [showOnlyCharacters, showOnlyThemes]);

  // Fonction pour ouvrir la page des versets thématiques
  const handleThemeClick = (theme) => {
    setSelectedTheme(theme);
  };

  // Fonction pour revenir de la page thématique
  const handleBackFromTheme = () => {
    setSelectedTheme(null);
  };

  // Fonction pour ouvrir la page d'histoire d'un personnage
  const handleCharacterClick = (character) => {
    setSelectedCharacterForHistory(character);
  };

  // Fonction pour revenir de la page d'histoire de personnage
  const handleBackFromCharacter = () => {
    setSelectedCharacterForHistory(null);
  };

  // Liste des 30 thèmes bibliques essentiels
  const biblicalThemes = [
    "Amour et Charité", "Foi et Confiance", "Espérance et Promesses", "Pardon et Miséricorde", 
    "Justice et Droiture", "Sagesse et Connaissance", "Prière et Adoration", "Paix et Réconciliation",
    "Joie et Louange", "Humilité et Service", "Courage et Force", "Patience et Persévérance",
    "Compassion et Bonté", "Vérité et Sincérité", "Liberté et Délivrance", "Guérison et Restauration",
    "Famille et Relations", "Travail et Vocation", "Richesse et Pauvreté", "Souffrance et Épreuves",
    "Mort et Résurrection", "Création et Nature", "Prophétie et Révélation", "Royaume de Dieu",
    "Salut et Rédemption", "Saint-Esprit", "Église et Communauté", "Mission et Évangélisation",
    "Sanctification", "Eschatologie et Fin des Temps"
  ];

  // Liste des personnages bibliques principaux
  // Personnages de l'Ancien Testament
  const oldTestamentCharacters = [
    "Adam", "Ève", "Caïn", "Abel", "Énoch", "Noé", "Sem", "Cham", "Japhet",
    "Abraham", "Sarah", "Lot", "Isaac", "Rébecca", "Jacob", "Rachel", "Léa", "Dina",
    "Joseph", "Juda", "Benjamin", "Tamar", "Éphraïm", "Manassé",
    "Moïse", "Aaron", "Miriam", "Josué", "Caleb",
    "Déborah", "Barak", "Gédéon", "Samson", "Ruth", "Rahab", "Jaël",
    "Samuel", "Eli", "Anne", "Saül", "Jonathan", "David", "Abigaïl", "Bethsabée",
    "Absalom", "Salomon", "Reine de Saba",
    "Élie", "Élisée", "Naaman",
    "Ésaïe", "Jérémie", "Ézéchiel", "Daniel",
    "Néhémie", "Esdras", "Esther", "Job"
  ];

  // Personnages du Nouveau Testament
  const newTestamentCharacters = [
    "Jésus", "Marie (mère de Jésus)", "Joseph (père terrestre)",
    "Jean-Baptiste", "Élisabeth", "Zacharie",
    "Pierre", "André", "Jacques (fils de Zébédée)", "Jean (l'apôtre)",
    "Philippe", "Barthélemy", "Matthieu", "Thomas",
    "Jacques (fils d'Alphée)", "Thaddée", "Simon le Zélote", "Judas Iscariote",
    "Marie-Madeleine", "Marthe", "Marie (sœur de Marthe)", "Lazare",
    "Nicodème", "Joseph d'Arimathée", "Zachée", "La Samaritaine",
    "Paul (Saul)", "Barnabas", "Silas", "Timothée", "Tite",
    "Luc", "Marc", "Étienne", "Philippe (l'évangéliste)",
    "Corneille", "Lydie", "Priscille", "Aquilas",
    "Hérode", "Pilate", "Caïphe", "Anne (le souverain sacrificateur)"
  ];

  // Liste combinée pour la compatibilité (si besoin)
  const biblicalCharacters = [...oldTestamentCharacters, ...newTestamentCharacters].sort();

  // Plus besoin de cette fonction - les personnages ouvrent maintenant une page dédiée

  // Fonction pour obtenir les résultats uniques
  const getUniqueResults = (results) => {
    const seen = new Set();
    const uniqueResults = [];
    
    for (const result of results) {
      const key = `${result.book}_${result.chapter}_${result.verse}`;
      if (!seen.has(key)) {
        seen.add(key);
        uniqueResults.push(result);
      }
    }
    
    return uniqueResults.slice(0, 10);
  };

  const searchBibleConcordance = (searchTerm) => {
    if (!searchTerm || searchTerm.trim().length < 2) {
      alert("Veuillez entrer au moins 2 caractères pour la recherche.");
      return;
    }

    // Construire l'URL selon la source choisie
    let concordanceUrl;
    let sourceName;
    
    if (concordanceSource === "levangile") {
      concordanceUrl = `https://www.levangile.com/Concordance-Multi-Bible.php?Version=LSG&Terme=${encodeURIComponent(searchTerm)}&option=1`;
      sourceName = "levangile.com";
    } else {
      // topbible
      concordanceUrl = `https://topbible.topchretien.com/search/?q=${encodeURIComponent(searchTerm)}&version=LSG`;
      sourceName = "topbible.topchretien.com";
    }
    
    window.open(concordanceUrl, '_blank');
    
    // Message de confirmation
    setResults([{
      book: "Concordance",
      chapter: "",
      verse: "",
      text: `🔍 Recherche de "${searchTerm}" ouverte dans la concordance biblique en ligne (${sourceName})`,
      reference: "Bible Louis Segond"
    }]);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    searchBibleConcordance(searchTerm);
  };

  const handleSuggestionClick = (term) => {
    setSearchTerm(term);
    searchBibleConcordance(term);
  };

  const openYouVersionConcordance = () => {
    const searchUrl = searchTerm 
      ? `https://www.bible.com/search/bible?q=${encodeURIComponent(searchTerm)}`
      : 'https://www.bible.com/';
    window.open(searchUrl, '_blank');
  };

  const highlightSearchTerm = (text, term) => {
    if (!term) return text;
    const regex = new RegExp(`(${term})`, 'gi');
    return text.replace(regex, '<mark style="background: #fef3c7; color: #92400e; padding: 2px 4px; border-radius: 4px;">$1</mark>');
  };

  // Gestion du rendu conditionnel pour les thèmes
  if (selectedTheme) {
    return (
      <ThemeVersesPage
        theme={selectedTheme}
        onGoBack={handleBackFromTheme}
      />
    );
  }

  // Gestion du rendu conditionnel pour les personnages
  if (selectedCharacterForHistory) {
    return (
      <CharacterHistoryPage
        character={selectedCharacterForHistory}
        onGoBack={handleBackFromCharacter}
      />
    );
  }

  return (
    <div className="sub-page">
      {/* Header */}
      <div className="sub-header">
        <button className="sub-back" onClick={onGoBack}>← Retour</button>
        <div className="sub-header-center">
          <div className="sub-title">Concordance Biblique</div>
          <div className="sub-sub">
            {currentTab === 'concordance' && 'Recherche de termes'}
            {currentTab === 'themes' && '30 thèmes essentiels'}
            {currentTab === 'personnages' && 'Personnages bibliques'}
          </div>
        </div>
        <div className="sub-actions">
          <button
            className={`sub-btn${currentTab === 'concordance' ? ' accent' : ''}`}
            onClick={() => setCurrentTab('concordance')}
          >Concordance</button>
          <button
            className={`sub-btn${currentTab === 'themes' ? ' accent' : ''}`}
            onClick={() => setCurrentTab('themes')}
          >Thèmes</button>
          <button
            className={`sub-btn${currentTab === 'personnages' ? ' accent' : ''}`}
            onClick={() => setCurrentTab('personnages')}
          >Personnages</button>
        </div>
      </div>

      {/* Body */}
      <div className="sub-body">
        <div className="sub-content">

          {currentTab === 'concordance' && (
            <div>
              <div className="sub-card">
                <div className="sub-card-title">Recherche dans la Concordance Biblique</div>
                <div style={{ marginBottom: '14px' }}>
                  <label className="sub-card-title" style={{ display: 'block', marginBottom: '6px' }}>
                    Source de concordance
                  </label>
                  <select
                    value={concordanceSource}
                    onChange={(e) => setConcordanceSource(e.target.value)}
                    className="sub-input"
                  >
                    <option value="levangile">levangile.com — Concordance Multi-Bible (29 versions)</option>
                    <option value="topbible">topbible.topchretien.com — TopBible (Interface moderne)</option>
                  </select>
                </div>
                <form onSubmit={handleSearchSubmit} style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Terme biblique (ex: amour, foi, espérance...)"
                    className="sub-input"
                    style={{ flex: 1 }}
                  />
                  <button type="submit" disabled={isLoading} className="sub-btn primary">
                    {isLoading ? '...' : 'Rechercher'}
                  </button>
                </form>
                <button type="button" onClick={openYouVersionConcordance} className="sub-btn" style={{ width: '100%' }}>
                  Ouvrir dans YouVersion Bible
                </button>
              </div>

              {isLoading && (
                <div className="sub-empty">
                  <div className="empty-icon">🔍</div>
                  <p>Recherche en cours...</p>
                </div>
              )}

              {results.length > 0 && (
                <div>
                  <div className="sub-section-header">
                    Résultats pour "{searchTerm}" ({results.length})
                  </div>
                  {results.map((result, index) => (
                    <div key={index} className="sub-result-item">
                      <div className="sub-result-ref">
                        {result.reference || `${result.book} ${result.chapter}:${result.verse}`}
                      </div>
                      <div
                        className="sub-result-text"
                        dangerouslySetInnerHTML={{ __html: highlightSearchTerm(result.text, searchTerm) }}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {currentTab === 'themes' && (
            <div>
              <div className="sub-section-header">30 Thèmes Essentiels de la Sainte Bible</div>
              <div className="sub-grid">
                {biblicalThemes.map((theme, index) => (
                  <button
                    key={theme}
                    onClick={() => handleThemeClick(theme)}
                    className="sub-grid-btn"
                  >
                    <span className="sub-grid-btn-label">{`${index + 1}. ${theme}`}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {currentTab === 'personnages' && (
            <div>
              <div className="sub-section-header">Ancien Testament</div>
              <div className="sub-grid">
                {oldTestamentCharacters.map(character => (
                  <button
                    key={character}
                    onClick={() => handleCharacterClick(character)}
                    className="sub-grid-btn"
                  >
                    <span className="sub-grid-btn-label">{character}</span>
                  </button>
                ))}
              </div>

              <div className="sub-section-header" style={{ marginTop: '28px' }}>Nouveau Testament</div>
              <div className="sub-grid">
                {newTestamentCharacters.map(character => (
                  <button
                    key={character}
                    onClick={() => handleCharacterClick(character)}
                    className="sub-grid-btn"
                  >
                    <span className="sub-grid-btn-label">{character}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default BibleConcordancePage;